import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import Dashboard from "./Dashboard";
import Inquiries from "./Inquiries";
import Bookings from "./Bookings";
import Services from "./Services";
import Workshops from "./Workshops";
import Posts from "./Posts";
import Testimonials from "./Testimonials";
import Availability from "./Availability";
import { LayoutDashboard, Inbox, CalendarCheck, Sparkles, CalendarDays, FileText, MessageSquareQuote, Clock, LogOut, Menu, X } from "lucide-react";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, C: Dashboard },
  { key: "inquiries", label: "Enquiries", icon: Inbox, C: Inquiries },
  { key: "bookings", label: "Bookings", icon: CalendarCheck, C: Bookings },
  { key: "workshops", label: "Workshops", icon: CalendarDays, C: Workshops },
  { key: "services", label: "Services", icon: Sparkles, C: Services },
  { key: "posts", label: "Blog", icon: FileText, C: Posts },
  { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote, C: Testimonials },
  { key: "availability", label: "Availability", icon: Clock, C: Availability },
];

function Login() {
  useSeo({ title: "Admin Sign In", path: "/admin", noindex: true });
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signIn = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-parchment px-6">
      <div className="bg-surface-card border hairline rounded-lg p-10 max-w-md w-full text-center">
        <div className="text-4xl font-display text-earth mb-2" style={{ fontWeight: 600 }}>अन्वय</div>
        <p className="label-caps text-earth/50 mb-6">Admin panel</p>
        <h1 className="text-2xl text-earth mb-2" style={{ fontWeight: 600 }}>Welcome back, Arjunna</h1>
        <p className="text-earth/60 mb-8">Sign in with Google to manage your practice.</p>
        <button onClick={signIn} className="btn-gold w-full" data-testid="google-signin-btn">
          Sign in with Google
        </button>
        <p className="text-xs text-earth/40 mt-6">Only authorized accounts can access this panel.</p>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [view, setView] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  useSeo({ title: "Admin", path: "/admin", noindex: true });

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-parchment"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Login />;

  const Active = NAV.find((n) => n.key === view)?.C || Dashboard;

  return (
    <div className="min-h-screen bg-parchment flex" data-testid="admin-panel">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-footer-bg text-footer-fg flex-col ${menuOpen ? "flex" : "hidden lg:flex"}`}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div><div className="text-2xl font-display" style={{ fontWeight: 600 }}>अन्वय</div><p className="text-xs text-footer-fg/50 uppercase tracking-wider">Admin</p></div>
          <button className="lg:hidden text-footer-fg/70" onClick={() => setMenuOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => { setView(n.key); setMenuOpen(false); }} data-testid={`nav-admin-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors ${view === n.key ? "bg-gold text-earth" : "text-footer-fg/70 hover:bg-white/5"}`}>
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="min-w-0"><p className="text-sm font-semibold truncate">{user.name}</p><p className="text-xs text-footer-fg/50 truncate capitalize">{user.role}</p></div>
          </div>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-semibold text-footer-fg/70 hover:bg-white/5" data-testid="admin-logout"><LogOut size={18} /> Sign out</button>
        </div>
      </aside>
      {menuOpen && <div className="fixed inset-0 bg-earth/40 z-40 lg:hidden" onClick={() => setMenuOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden flex items-center justify-between p-4 border-b hairline bg-surface-card sticky top-0 z-30">
          <button onClick={() => setMenuOpen(true)} className="text-earth" data-testid="admin-menu-toggle"><Menu size={24} /></button>
          <span className="font-display text-xl text-earth">अन्वय Admin</span>
          <div className="w-6" />
        </header>
        <main className="p-5 md:p-8 max-w-5xl mx-auto"><Active /></main>
      </div>
    </div>
  );
}
